import React, { JSX } from "react";
import styled, { css } from "styled-components";
import { theme } from "@ui/styles/theme";

interface BadgeLabelProps extends React.HTMLAttributes<HTMLDivElement> {
	renderIcons?: () => JSX.Element;
	bgColor?: string;
	isHovered: boolean;
	badgeTitle?: string;
	hoverWidth: number;
}

export const BadgeLabel: React.FC<BadgeLabelProps> = ({
	renderIcons,
	isHovered,
	badgeTitle,
	hoverWidth,
	bgColor = theme.colors.secondary,
	...htmlProps
}) => {
	return (
		<Container bgColor={bgColor} isHovered={isHovered} hoverWidth={hoverWidth} {...htmlProps}>
			<StyledIcons isHovered={isHovered}>{renderIcons?.()}</StyledIcons>
			<BadgeTitle isHovered={isHovered}>{badgeTitle}</BadgeTitle>
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => !["bgColor", "isHovered", "hoverWidth"].includes(prop),
})<{ bgColor?: string; isHovered?: boolean; hoverWidth?: number }>`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	background-color: ${({ bgColor }) => bgColor};
	border-radius: 0 0 20px 20px;
	width: 52px;
	height: 47px;
	transition: width 0.5s linear;
	-webkit-transition: all 1s linear;
	-moz-transition: all 1s linear;
	-o-transition: all 1s linear;
	${({ isHovered, hoverWidth }) =>
		isHovered &&
		css`
			width: ${hoverWidth}px;
			height: 47px;
			border-radius: 0 0 20px 20px;
			animation: slidein 0.5s;
			-moz-animation: slidein 0.5s;
			-webkit-animation: slidein 0.5s;
			-o-animation: slidein 0.5s;
			@keyframes slidein {
				from {
					width: 52px;
				}

				to {
					width: ${hoverWidth}px;
				}
			}
		`}
`;

const StyledIcons = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isHovered",
})<{ isHovered?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #fff;
	border-radius: 50%;
	width: 31px;
	height: 31px;
	position: absolute;
	top: 6px;
	left: 11px;
`;

const BadgeTitle = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isHovered",
})<{ isHovered: boolean }>`
	color: #fff !important;
	font-size: 1rem !important;
	font-weight: 300 !important;
	${({ theme }) => theme.textStyles.title};
	position: absolute;
	top: 11px;
	right: 20px;
	opacity: 0;

	${({ isHovered }) =>
		isHovered
			? css`
					transition: opacity 0.5s 0.2s linear;
					-webkit-transition: opacity 0.1s 0.5s;
					-moz-transition: opacity 0.1s 0.5s;
					-o-transition: opacity 0.1s 0.5s;
					opacity: 1;
				`
			: css`
					transition: opacity 0.5s;
					-webkit-transition: opacity 0.5s;
					-moz-transition: opacity 0.5s;
					-o-transition: opacity 0.5s;
				`};
`;
